import { TIMER_EXTEND_MAX_SEC } from '@skinstep/shared';
import type { ReactNode } from 'react';
import type { Lot } from '../data/mockLots';
import { useCountdown } from '../hooks/useCountdown';
import type { LotState } from '../hooks/useLotState';
import { resolveFinish, resolveRarity } from '../lib/rarity';
import { RingTimer } from './RingTimer';
import { SkinFrame } from './SkinFrame';
import { SkinThumbnail } from './SkinThumbnail';

const WEAR_LABEL: Record<string, string> = {
  FN: 'Factory New',
  MW: 'Minimal Wear',
  FT: 'Field-Tested',
  WW: 'Well-Worn',
  BS: 'Battle-Scarred',
};

type Props = {
  lot: Lot;
  state: LotState;
  livePrice?: number;
  flashTrigger?: number;
  timerEndsAt?: number;
  rightSlot?: ReactNode;
};

export function LotHero({
  lot,
  state,
  livePrice,
  flashTrigger = 0,
  timerEndsAt,
  rightSlot,
}: Props) {
  const liveSeconds = useCountdown(timerEndsAt ?? lot.timerEndsAt);
  const startsIn = useCountdown(lot.startsAt ?? lot.timerEndsAt);

  const rarity = resolveRarity(lot);
  const finish = resolveFinish(lot);
  const wearLabel = WEAR_LABEL[lot.wear] ?? lot.wear;

  const shownPrice =
    state === 'closed' ? (lot.finalPrice ?? lot.currentPrice) : (livePrice ?? lot.currentPrice);
  const discountPct = Math.round((1 - shownPrice / lot.marketPrice) * 100);

  return (
    <section className="relative hud-corners overflow-hidden">
      <div className="relative" style={{ aspectRatio: '4 / 3' }}>
        <div className="absolute inset-0">
          {lot.imageUrl ? (
            <SkinThumbnail
              imageUrl={lot.imageUrl}
              alt={`${lot.weaponLabel} | ${lot.skinName}`}
              weapon={lot.weapon}
              palette={lot.palette}
              rarityColor={lot.rarityColor ?? null}
              size="lg"
            />
          ) : (
            <SkinFrame weapon={lot.weapon} palette={lot.palette} size="lg" />
          )}
        </div>

        <div
          className="absolute inset-0 pointer-events-none scanlines mix-blend-overlay"
          style={{ opacity: state === 'closed' ? 0.25 : 0.55 }}
        />

        {state === 'live' && !lot.imageUrl && (
          <div className="absolute inset-x-0 top-1/3 h-12 overflow-hidden pointer-events-none">
            <div
              className="absolute inset-y-0 anim-sweep"
              style={{
                width: '38%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
              }}
            />
          </div>
        )}

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,14,20,0.55) 0%, rgba(10,14,20,0.05) 38%, rgba(10,14,20,0.92) 100%)',
          }}
        />

        <div className="relative z-10 h-full flex flex-col">
          <div className="flex items-start justify-end p-4 pb-2 gap-3">
            <div
              className="flex flex-col items-end justify-center shrink-0"
              style={{ width: 96, height: 96 }}
            >
              {state === 'live' && (
                <RingTimer
                  secondsLeft={liveSeconds}
                  timerEndsAt={timerEndsAt ?? lot.timerEndsAt}
                  size={96}
                  stroke={5}
                  scaleSec={TIMER_EXTEND_MAX_SEC}
                  flashTrigger={flashTrigger}
                  showLabel
                  label="TIMER"
                />
              )}
              {state === 'scheduled' &&
                (startsIn > 0 ? (
                  <RingTimer
                    variant="upcoming"
                    secondsLeft={startsIn}
                    size={96}
                    stroke={5}
                    showLabel
                    label="OPENS IN"
                  />
                ) : (
                  // Start time reached; the lot's DO is going live but the
                  // polled home list hasn't propagated `started_at` yet (~1 poll
                  // window). Show an active "opening" beat instead of a frozen
                  // 00:00 until the live timer arrives.
                  <span
                    className="font-mono font-bold uppercase tracking-[0.18em] text-primary leading-none animate-pulse"
                    style={{ fontSize: 15, textShadow: '0 0 14px rgba(0,229,255,0.45)' }}
                  >
                    Opening…
                  </span>
                ))}
              {state === 'closed' && (
                <RingTimer
                  variant="closed"
                  secondsLeft={0}
                  closedAt={lot.closedAt}
                  size={96}
                  stroke={5}
                  showLabel
                  label="CLOSED"
                />
              )}
            </div>
          </div>

          <div className="mt-auto px-4 pb-4 flex flex-col gap-3">
            <div className="min-w-0">
              {finish && (
                <span
                  className="font-mono font-bold text-[10px] tracking-[0.18em] uppercase block leading-none mb-1.5"
                  style={{ color: finish.color, textShadow: `0 0 10px ${finish.color}73` }}
                >
                  {finish.label}
                </span>
              )}
              <h2
                className="font-display font-bold leading-[0.92] tracking-tight text-fg truncate"
                style={{ fontSize: 38 }}
              >
                {lot.weaponLabel}
              </h2>
              <h3
                className="font-display font-medium leading-tight mt-1 truncate"
                style={{ fontSize: 18, color: lot.palette.accent ?? lot.palette.to }}
              >
                | {lot.skinName}
              </h3>
            </div>

            <div
              className="flex items-center gap-1.5 font-mono text-[10px] leading-none tracking-[0.22em] pt-2.5 border-t border-edge/60"
              title={wearLabel}
            >
              <span
                aria-hidden
                className="inline-block w-[7px] h-[7px] rounded-full shrink-0 -translate-y-px"
                style={{
                  background: rarity.color,
                  boxShadow: `0 0 8px ${rarity.color}`,
                }}
              />
              <span style={{ color: rarity.color }}>{rarity.label}</span>
              <span className="text-fg-dim mx-0.5">·</span>
              <span className="text-fg/85">{wearLabel}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative bg-bg-elev/95 border-t border-edge px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col min-w-0">
            <span className="font-mono text-[9px] tracking-[0.3em] text-fg-muted">
              {state === 'closed' ? 'FINAL PRICE' : 'CURRENT PRICE'}
            </span>
            <div className="flex items-baseline gap-1.5">
              <span
                className="font-mono font-bold text-[32px] tabular-nums leading-none"
                style={{
                  color: state === 'closed' ? '#e6edf3' : '#00ff94',
                  textShadow: state === 'closed' ? 'none' : '0 0 14px rgba(0,255,148,0.35)',
                }}
              >
                ${shownPrice.toFixed(2)}
              </span>
              {state === 'live' && (
                <span className="font-mono text-[11px] text-accent">+$0.01</span>
              )}
            </div>
            <span className="font-mono text-[10px] tracking-wider text-fg-muted mt-1">
              MARKET ${lot.marketPrice.toFixed(2)}
              {shownPrice > 0 && <span className="text-success ml-1">−{discountPct}%</span>}
            </span>
          </div>

          {rightSlot}
        </div>

        <div className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      </div>
    </section>
  );
}
